"use client"

import type React from "react"
import { useState, useRef, useEffect } from "react"
import { ThemeProvider } from '@mui/material/styles'
import { Box, Typography, TextField, Button } from '@mui/material'
import MessageList from "@/components/chat/MessageList"
import { Send, Mic } from '@mui/icons-material'
import type { Message } from "@/pages/chat/types/chat"
import PageHeader from "@/layout/PageHeader"
import { useChatContext } from "@/context/ChatContext"
import theme from '@/theme'

// Array of profound stoic-themed responses
const SIMULATED_RESPONSES = [
    "Accept what you cannot change, and focus your energy on what lies within your control. The cards remind us that true power comes from mastering our reactions, not controlling external events.",
    "In the silence between thoughts lies wisdom. The cards suggest that your greatest teacher is the quiet observation of your own mind.",
    "Fortune is like the market - it will rise and fall. Your virtue, however, is the bedrock that remains constant through all seasons of life.",
    "The cards reveal that your obstacles are not your enemies, but your teachers. Each challenge is an opportunity to practice wisdom, justice, courage, and self-control.",
    "What stands in the way becomes the way. The very thing you perceive as a barrier may be the path to your greatest growth.",
    "Remember that all is temporary - both pleasure and pain. The cards suggest finding strength in impermanence rather than fighting against it.",
    "Your worth is not in what happens to you, but in how you respond. The cards show that dignity comes not from external circumstances, but from internal character.",
    "Live according to nature, the cards suggest. True happiness comes not from forcing life to meet your expectations, but from aligning yourself with what is.",
    "The cards remind us: We suffer not from events themselves, but from our judgments about them. Consider viewing your situation with the eyes of a sage.",
    "Wealth lies not in having much, but in needing little. The cards suggest finding abundance in simplicity and peace in acceptance.",
    "Like a rock that splits the stream, remain unmoved by praise or blame. Your true nature is beyond the opinions of others.",
    "The cards reveal that this moment - exactly as it is - contains everything you need for your growth. Wisdom lies in seeing the perfection of the present."
];

function ChatPage() {
    const [input, setInput] = useState("")
    const [isRecording, setIsRecording] = useState(false)
    const messagesEndRef = useRef<HTMLDivElement>(null)
    const { messages, addMessage, isTyping, setIsTyping } = useChatContext()

    // Auto-scroll to bottom when messages change or typing status changes
    useEffect(() => {
        const scrollToBottom = () => {
            if (messagesEndRef.current) {
                messagesEndRef.current.scrollIntoView({ behavior: 'smooth' })
            }
        }
        scrollToBottom()
    }, [messages, isTyping]) // Re-run when messages or typing status changes

    // Simulated message handling without API call
    const handleSendMessage = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault()
        if (!input.trim()) return

        // Add user message
        const userMessage: Message = {
            id: Date.now().toString(),
            content: input,
            role: "user" as const,
            timestamp: new Date(),
        }
        addMessage(userMessage)
        setInput("")

        // Show typing indicator
        setIsTyping(true)

        // Simulate API delay
        await new Promise(resolve => setTimeout(resolve, 1000));

        // Get random response
        const randomResponse = SIMULATED_RESPONSES[Math.floor(Math.random() * SIMULATED_RESPONSES.length)];

        // Add simulated AI response
        addMessage({
            id: Date.now().toString(),
            content: randomResponse,
            role: "assistant" as const,
            timestamp: new Date(),
        })

        setIsTyping(false)
    }

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setInput(e.target.value)
    }

    const handleRecordToggle = () => {
        setIsRecording(!isRecording)
    }

    return (
        <ThemeProvider theme={theme}>
            <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                height: '100vh',
                bgcolor: '#F8F8F8'
            }}>
                <PageHeader titleKey={'pages.chat' as any} />

                <Box sx={{
                    mt: 3,
                    flex: 1,
                    overflow: 'auto',
                    p: 3,
                    bgcolor: 'background.default'
                }}>
                    <MessageList messages={messages} isTyping={isTyping} />
                    <div ref={messagesEndRef} />
                </Box>

                <Box
                    component="form"
                    onSubmit={handleSendMessage}
                    sx={{
                        p: 2,
                        borderTop: '1px solid rgba(201, 173, 167, 0.4)',
                        bgcolor: 'background.paper'
                    }}
                >
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end' }}>
                        <TextField
                            fullWidth
                            placeholder={isRecording ? "Recording..." : "Type your message..."}
                            value={input}
                            onChange={handleInputChange}
                            disabled={isRecording}
                            multiline
                            maxRows={4}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    backgroundColor: 'rgba(255,255,255,0.7)',
                                    borderRadius: '12px',
                                    border: '1px solid rgba(201, 173, 167, 0.4)',
                                    '& fieldset': {
                                        borderColor: 'transparent',
                                    },
                                    '&:hover fieldset': {
                                        borderColor: 'transparent',
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: 'transparent',
                                    },
                                },
                                '& .MuiInputBase-input': {
                                    fontFamily: '"Plus Jakarta Sans", sans-serif',
                                    fontSize: '1rem',
                                },
                            }}
                        />
                        {input.trim() ? (
                            <Button
                                type="submit"
                                variant="contained"
                                sx={{
                                    minWidth: '50px',
                                    height: '50px',
                                    borderRadius: '12px',
                                    bgcolor: '#8B5B52',
                                    '&:hover': {
                                        bgcolor: '#7A4F47'
                                    }
                                }}
                            >
                                <Send />
                            </Button>
                        ) : (
                            <Button
                                onClick={handleRecordToggle}
                                variant="contained"
                                sx={{
                                    minWidth: '50px',
                                    height: '50px',
                                    borderRadius: '12px',
                                    bgcolor: isRecording ? '#DC2626' : '#8B5B52',
                                    '&:hover': {
                                        bgcolor: isRecording ? '#B91C1C' : '#7A4F47'
                                    }
                                }}
                            >
                                <Mic />
                            </Button>
                        )}
                    </Box>
                </Box>
            </Box>
        </ThemeProvider>
    )
}

export default ChatPage;

